import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './GeneratedSceneItem.module.scss';
import cx from 'classnames';
import { t } from 'i18next';
import { DebounceInput } from 'react-debounce-input';
import { diffWords } from 'diff';
import { BACKSPACE_KEY_CODE, ESCAPE_KEY_CODE } from '../../constants/common';
import { useTranslation } from 'react-i18next';
import { secondsInMinute } from 'date-fns/constants';
import format from 'date-fns/format';

// Font size is 16px
const FONT_SIZE = 16;
// Line height is 160%
const LINE_HEIGHT = 1.6;
const ONE_TEXT_LINE_HEIGHT = FONT_SIZE * LINE_HEIGHT;
const MAX_ROWS_WITH_COLLAPSE = 3;
const DEFAULT_WORDS_COUNT = 150
const DEFAULT_WPS = DEFAULT_WORDS_COUNT / secondsInMinute; //Average word per minute count whilst speaking is around 125

// variant: primary | secondary

const TextComponent = ({
  title = '',
  countLength = false,
  text = '',
  onSaveChanges,
  className,
  placeholder,
  linesWithCollapse = MAX_ROWS_WITH_COLLAPSE,
  changeTextEdit = () => { },
  disableEditing,
  collapsable = true,
  variant = 'primary',
  compareTo = null,
  checkbox = null,
}) => {
  const [collapse, setCollapse] = useState(true);
  const [collapsedText, setCollapsedText] = useState(text ? text.split('\n') : []);
  const [edit, setEdit] = useState(false);
  const [textareaRows, setTextareaRows] = useState(0);
  const [startMinHeight, setStartMinHeight] = useState(ONE_TEXT_LINE_HEIGHT);
  const [init, setInit] = useState(false);

  const { t } = useTranslation();

  const textRef = useRef();
  const containerRef = useRef();
  const updatedTextValueRef = useRef(text);

  const getTextLengthString = useCallback((text, wps = DEFAULT_WPS) => {
    if (!text || text?.trim()?.length === 0) {
      return '';
    }
    const wordsPerSec = wps > 0 ? wps : DEFAULT_WPS;
    const words = text.trim().replace("\n", " ").split(" ");
    let timeString = '';
    let seconds = 0;
    if (text.trim().length > 0) {
      seconds = Math.round(Math.max(1, words.length / wordsPerSec));
    }
    if (seconds < secondsInMinute) {
      timeString = `${seconds} ${t('general.time.seconds')}`;
    } else {
      const date = new Date(0); // Create a new date object at Unix epoch
      date.setSeconds(seconds); // Set seconds from the input
      timeString = `${format(date, 'm:ss')} ${t('general.time.minutes')}`;
    }
    return `(${t('storyboard.editor.script_length')}: ${timeString})`
  }, [t]);

  const textToCalculate = useMemo(() => {
    return edit ? updatedTextValueRef.current : text;
  }, [edit, updatedTextValueRef?.current, text]);


  const toggleCollapse = useCallback(
    event => {
      event?.stopPropagation();

      !collapse &&
        setTimeout(() => {
          updateCollapsedText();
        }, 150);
      setCollapse(prevState => !prevState);
    },
    [collapse, updateCollapsedText]
  );

  const showMoreLink = useMemo(() => {
    return collapsable ? (
      <a style={{ cursor: 'pointer', display: 'block' }} id="collapse-button" onClick={toggleCollapse}>
        {collapse ? t('storyboard.editor.show_more') : t('storyboard.editor.show_less')}
      </a>
    ) : (
      ''
    );
  }, [collapsedText, text, collapse, toggleCollapse, collapsable]);

  const getParagraphsList = (textLines, isTextCollapsable, link, compareTo = '') => {
    const compareToLines = compareTo?.split('\n') ?? [];
    return textLines?.map((text, index, array) => {
      if (compareToLines?.length) {
        const diff = diffWords(compareToLines[index] ?? '', text, { newlineIsToken: true });
        const comparedResult = diff.filter(part => part.added || !part.removed);
        return (
          <p className={styles.textLine}>
            {comparedResult.map((part, index) => (
              <span key={index} className={cx({ [styles.highlight]: part.added })}>
                {part.value}
              </span>
            ))}
          </p>
        );
      }

      return (
        <p className={styles.textLine}>
          {text}
          {isTextCollapsable && index === array.length - 1 ? <> {link}</> : null}
        </p>
      );
    });
  };

  const showText = useMemo(() => {
    if (!collapsedText || !text) {
      return placeholder;
    }
    const textByNewLine = [];
    if (!collapsable || !collapse) {
      textByNewLine.push(...text?.split('\n'));
    } else {
      textByNewLine.push(...collapsedText);
    }
    return getParagraphsList(textByNewLine, textareaRows > linesWithCollapse, showMoreLink, compareTo);
  }, [collapse, collapsedText, text, placeholder, textareaRows, linesWithCollapse, showMoreLink, collapsable, compareTo]);

  const onUpdateChanges = () => {
    if (edit) {
      if (text !== updatedTextValueRef.current) {
        onSaveChanges(updatedTextValueRef.current);
        updateTextareaRows();

        if (textareaRows > linesWithCollapse && collapse) {
          toggleCollapse();
        }
      }

      setEdit(false);
    }
  };

  const handleEnableEditing = () => {
    updatedTextValueRef.current = text;

    textareaRows > linesWithCollapse && setCollapse(true);

    setTimeout(() => {
      setEdit(true);
      initiateTextareaRows();
    }, 150);
  };

  const getTextAreaRows = measuredText => {
    const parentDiv = document.getElementById(`container-${title}`);
    const textDiv = document.createElement('div');
    const splittedTextContent = measuredText?.split('\n').map(item => {
      const paragraph = document.createElement('p');
      paragraph.style.margin = 0;
      paragraph.style.padding = 0;
      paragraph.style.fontSize = '16px';
      paragraph.style.fontWeight = 400;
      paragraph.style.lineHeight = `${startMinHeight}px`;
      paragraph.style.minHeight = `${startMinHeight}px`;
      paragraph.style.width = '100%';
      paragraph.style.overflowWrap = 'anywhere';
      const textNode = document.createTextNode(item);
      paragraph.appendChild(textNode);
      textDiv.appendChild(paragraph);
      return paragraph;
    });
    parentDiv.appendChild(textDiv);

    let totalRows = 0;
    if (Math.round(textDiv?.getBoundingClientRect().height) >= startMinHeight) {
      totalRows = Math.round(textDiv?.getBoundingClientRect().height / startMinHeight);
    } else if (textDiv?.getBoundingClientRect().height === 0) {
      totalRows = 1;
    } else {
      totalRows = linesWithCollapse;
    }

    const splittedText = splittedTextContent?.map(text => {
      const innerText = text.innerText;
      const rows = Math.round(text.getBoundingClientRect().height / startMinHeight);
      textDiv.removeChild(text);
      return { text: innerText, rows };
    });
    parentDiv.removeChild(textDiv);
    return { totalRows, splittedText };
  };

  const updateCollapsedText = useCallback(() => {
    if (!text) {
      setCollapsedText([]);
    }
    let linkTextLength = 14;
    const link = document.getElementById('collapse-button');
    if (link) {
      linkTextLength = link.innerText.length + 4;
    }
    const { totalRows: calculatedRows, splittedText } = getTextAreaRows(text);

    const textRows = calculatedRows !== textareaRows ? calculatedRows : textareaRows;
    calculatedRows !== textareaRows && setTextareaRows(calculatedRows);
    if (textRows > linesWithCollapse) {
      let collapsedRows = 0;
      const collapsedLines = splittedText
        .map(({ text, rows }) => {
          if (collapsedRows < linesWithCollapse) {
            const nextCollapsedLines = collapsedRows + rows;
            if (collapsedRows + rows < linesWithCollapse) {
              collapsedRows = nextCollapsedLines;
              return text;
            } else {
              // How many lines from current text should be sliced
              const linesPossible = linesWithCollapse - collapsedRows;
              const parentDiv = document.getElementById(`container-${title}`);
              const paragraphElement = document.createElement('p');
              paragraphElement.style.margin = 0;
              paragraphElement.style.padding = 0;
              paragraphElement.style.fontSize = '16px';
              paragraphElement.style.fontWeight = 400;
              paragraphElement.style.lineHeight = `${startMinHeight}px`;
              paragraphElement.style.overflowWrap = 'anywhere';
              paragraphElement.style.width = '100%';
              paragraphElement.style.fontFamily = '"Nunito Sans", sans-serif';
              const showMoreTextLength = collapsable ? t('storyboard.editor.show_more').length : 0;
              // Do an approximation (for example if text is two lines - slice text in half)
              let sliceIndex = (text.length * linesPossible) / rows - showMoreTextLength;
              // Calculate an addition (could be + or -) to show ... Show more at the end of the string
              let fitsTheLine = false;
              let fitsParentWidth = false;
              parentDiv.appendChild(paragraphElement);
              while (!fitsTheLine) {
                paragraphElement.innerText = `${text.slice(0, sliceIndex)}... ${t('storyboard.editor.show_more')}`;
                const lines = Math.round(paragraphElement.getBoundingClientRect().height / startMinHeight);
                if (lines < linesPossible) {
                  sliceIndex += 1;
                } else if (lines === linesPossible) {
                  if (sliceIndex === text.length + showMoreTextLength || sliceIndex <= 0) {
                    fitsTheLine = true;
                  } else {
                    fitsParentWidth = true;
                    sliceIndex += 1;
                  }
                } else {
                  if (fitsParentWidth) {
                    fitsTheLine = true;
                  }
                  sliceIndex -= 1;
                }
              }
              parentDiv.removeChild(paragraphElement);
              collapsedRows += linesPossible;
              return text.slice(0, sliceIndex) + (sliceIndex > 0 && sliceIndex < text.length ? '...' : '');
            }
          }
          return null;
        })
        .filter(item => item !== null);

      setCollapsedText(collapsedLines);
      return;
    }
    setCollapsedText(text?.split('\n'));
  }, [text, textareaRows, setCollapsedText, getTextAreaRows]);

  const updateTextareaRows = () => {
    const div = document.getElementById(`textarea-${title}`);
    if (div?.getBoundingClientRect().height > startMinHeight) {
      setTextareaRows(Math.round(div?.getBoundingClientRect().height / startMinHeight));
    } else {
      setTextareaRows(linesWithCollapse);
    }
  };

  const initiateTextareaRows = () => {
    const { totalRows } = getTextAreaRows(text);
    setTextareaRows(totalRows);
  };

  const handleEdit = e => {
    updatedTextValueRef.current = e.target.value;
  };

  const handleKeyEvent = e => {
    // Reset field height
    e.target.style.height = 'inherit';
    // Get the computed styles for the element
    e.target.style.height = `${e.target.scrollHeight}px`;
  };

  const handleKeydown = event => {
    switch (event.keyCode) {
      case ESCAPE_KEY_CODE: {
        setEdit(false);
        break;
      }
      case BACKSPACE_KEY_CODE: {
        handleKeyEvent(event);
        break;
      }
    }
  };

  const handleClick = useCallback(
    e => {
      if (!disableEditing) {
        handleEnableEditing(e);
      }
    },
    [disableEditing, handleEnableEditing]
  );

  useEffect(() => {
    initiateTextareaRows();
    setInit(true);
  }, []);

  useEffect(() => {
    return () => {
      if (text !== updatedTextValueRef.current) {
        onSaveChanges(updatedTextValueRef.current);
      }
    }
  }, []);

  useEffect(() => {
    if (textareaRows > linesWithCollapse) {
      setCollapse(true);
    }
  }, [textareaRows]);

  useEffect(() => {
    collapsable && updateCollapsedText();
  }, [text, containerRef?.current?.offsetWidth, collapsable]);

  useEffect(() => {
    if (edit) {
      window.addEventListener('keydown', handleKeydown);
      const textarea = document.getElementById(`textarea-${title}`);
      if (textarea) {
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
    } else {
      window.removeEventListener('keydown', handleKeydown);
    }

    changeTextEdit(edit);
  }, [edit]);

  useEffect(() => {
    if (init) {
      textareaRows < linesWithCollapse && setStartMinHeight(textareaRows * ONE_TEXT_LINE_HEIGHT);
      setInit(false);
    }
  }, [textareaRows]);

  return (
    <div className={cx(styles.textWrapper, className)} div={containerRef}>
      <div className={cx(styles.titleContainer, { [styles.topPadding]: !title && !countLength })}>
        {title && <h4 className={styles.title}>{title}</h4>}
        {countLength && <h5 className={cx(styles.subTitle, { [styles.leftPadding]: !title })}>{getTextLengthString(textToCalculate)}</h5>}
      </div>
      <div className={styles.textContainer}>
        {checkbox && <div className={styles.checkboxContainer}>{checkbox}</div>}
        <div id={`container-${title}`} className={cx(styles.inputContainer, { [styles.edit]: edit }, styles[variant])}>
          {edit ? (
            <DebounceInput
              id={`textarea-${title}`}
              element="textarea"
              type="text"
              debounceTimeout={300}
              className={styles.textarea}
              inputRef={textRef}
              autoFocus
              onBlur={onUpdateChanges}
              value={updatedTextValueRef.current}
              placeholder={placeholder}
              onChange={handleEdit}
              onInput={handleKeyEvent}
              onPaste={handleKeyEvent}
            />
          ) : (
            <div
              id={`text-${title}`}
              style={{ minHeight: startMinHeight }}
              className={cx(styles.text, {
                [styles.collapse]: !collapse,
                [styles.placeholder]: text?.length === 0 || !text || collapsedText?.length === 0,
              })}
              onClick={handleClick}
            >
              {showText}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TextComponent;
